import { useState } from 'react';
import { useStore } from '../store';
import {
  ADVANCED_TOKENS,
  PRESETS,
  PRIMARY_TOKENS,
  DEFAULT_FONTS,
  TARGET_COLS,
  TERM_BASE_MAX,
  TERM_BASE_MIN,
  UI_SCALE_MAX,
  UI_SCALE_MIN,
  contrastRatio,
  deriveNeutrals,
  isHex,
  normalizeHex,
  presetById,
  type Theme,
  type ThemeTokens,
} from '../theme';

/**
 * Painel de Aparencia: escolher um preset e ajustar cor por cor.
 *
 * Nao existe "Cancelar" de proposito. A mudanca e aplicada na hora -- o preview
 * ao vivo e o proprio estado real, sem um segundo caminho de renderizacao para
 * divergir -- e o desfazer sao os presets e o "restaurar padrao", que estao a um
 * clique. Um par Salvar/Descartar exigiria manter dois temas em memoria e
 * decidir o que fazer quando o usuario fechasse o painel no meio.
 */
export function AppearancePanel() {
  const theme = useStore((s) => s.theme);
  const setTheme = useStore((s) => s.setTheme);
  const openPanel = useStore((s) => s.openPanel);
  const fonts = useStore((s) => s.fonts);
  const setFonts = useStore((s) => s.setFonts);
  const [advanced, setAdvanced] = useState(false);

  /** Toda edicao de token produz um tema "custom" que lembra de onde saiu. */
  function patch(tokens: Partial<ThemeTokens>) {
    const base = presetById(theme.id) ? theme.id : (theme as Theme & { baseId?: string }).baseId;
    setTheme({
      ...theme,
      id: 'custom',
      label: 'Personalizado',
      // `baseId` e o que permite herdar o bloco ANSI e os tokens nao editados
      // quando o tema volta do localStorage numa versao futura do app.
      ...{ baseId: base ?? theme.id },
      tokens: { ...theme.tokens, ...tokens },
    } as Theme);
  }

  // O acento tambem vira texto (aba em hover, borda do toast) e preenchimento
  // do botao primario. Avisar e melhor que silenciosamente deixar ilegivel --
  // mas nao bloqueamos: e a cor do usuario, a escolha e dele.
  const accentOnBg = contrastRatio(theme.tokens.accent, theme.tokens.bg);
  const textOnAccent = contrastRatio(theme.tokens.onAccent, theme.tokens.accent);

  return (
    <aside className="panel">
      <div className="panel-head">
        <h2>Aparência</h2>
        <span className="grow" />
        <button className="ghost" onClick={() => openPanel(null)} title="Fechar">
          ✕
        </button>
      </div>

      <div className="section-label">Tema</div>
      <div className="theme-presets">
        {PRESETS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            className={`theme-card${theme.id === preset.id ? ' selected' : ''}`}
            onClick={() => setTheme(preset)}
          >
            <span className="theme-swatches">
              {[preset.tokens.bg, preset.tokens.surface, preset.tokens.accent, preset.tokens.text].map(
                (c, i) => (
                  <span key={i} style={{ background: c }} />
                ),
              )}
            </span>
            {preset.label}
          </button>
        ))}
        {theme.id === 'custom' && (
          <button type="button" className="theme-card selected" disabled>
            <span className="theme-swatches">
              {[theme.tokens.bg, theme.tokens.surface, theme.tokens.accent, theme.tokens.text].map(
                (c, i) => (
                  <span key={i} style={{ background: c }} />
                ),
              )}
            </span>
            Personalizado
          </button>
        )}
      </div>

      <div className="section-label">Tamanho da fonte</div>

      <div className="font-row">
        <label>Interface</label>
        <span style={{ fontSize: 11, color: 'var(--text-dim)', textAlign: 'right' }}>
          {Math.round(fonts.ui * 100)}%
        </span>
        <input
          type="range"
          min={UI_SCALE_MIN}
          max={UI_SCALE_MAX}
          step={0.05}
          value={fonts.ui}
          aria-label="Escala da interface"
          onChange={(e) => setFonts({ ...fonts, ui: Number(e.target.value) })}
        />
      </div>

      <div className="font-row">
        <label>Terminal</label>
        <span style={{ fontSize: 11, color: 'var(--text-dim)', textAlign: 'right' }}>
          {fonts.termBase}px
        </span>
        <input
          type="range"
          min={TERM_BASE_MIN}
          max={TERM_BASE_MAX}
          step={0.5}
          value={fonts.termBase}
          aria-label="Tamanho base da fonte do terminal"
          onChange={(e) => setFonts({ ...fonts, termBase: Number(e.target.value) })}
        />
      </div>

      {/* .checkbox-row, e nao .row: a regra global de `input` da width 100%, o
          que estica a caixinha e joga o texto para a outra ponta. */}
      <div className="checkbox-row" style={{ marginTop: 6 }}>
        <input
          id="term-auto"
          type="checkbox"
          checked={fonts.termAuto}
          onChange={(e) => setFonts({ ...fonts, termAuto: e.target.checked })}
        />
        <label htmlFor="term-auto" style={{ cursor: 'pointer' }}>
          Encolher a fonte em panes estreitos
        </label>
      </div>

      <p className="card-desc" style={{ marginTop: 6 }}>
        Os TUIs do Claude, Codex e Gemini são desenhados para {TARGET_COLS} colunas. Num
        pane estreito a fonte encolhe até elas caberem — o terminal passa a mostrar{' '}
        <strong>mais</strong> conteúdo, não menos. Nunca abaixo de {fonts.termMin}px, e
        nunca acima do tamanho base acima.
      </p>

      {fonts.termAuto && (
        <div className="font-row">
          <label>Piso</label>
          <span style={{ fontSize: 11, color: 'var(--text-dim)', textAlign: 'right' }}>
            {fonts.termMin}px
          </span>
          <input
            type="range"
            min={6}
            max={fonts.termBase}
            step={0.5}
            value={fonts.termMin}
            aria-label="Piso da fonte do terminal"
            onChange={(e) => setFonts({ ...fonts, termMin: Number(e.target.value) })}
          />
        </div>
      )}

      <div className="row" style={{ marginTop: 6 }}>
        <span className="grow" />
        <button
          type="button"
          className="ghost"
          onClick={() => setFonts({ ...DEFAULT_FONTS })}
          disabled={
            fonts.ui === DEFAULT_FONTS.ui &&
            fonts.termBase === DEFAULT_FONTS.termBase &&
            fonts.termAuto === DEFAULT_FONTS.termAuto &&
            fonts.termMin === DEFAULT_FONTS.termMin
          }
        >
          restaurar fontes
        </button>
      </div>

      <div className="section-label">Ajuste fino</div>
      {PRIMARY_TOKENS.map(({ key, label }) => (
        <TokenRow
          key={key}
          label={label}
          value={theme.tokens[key]}
          onChange={(v) => patch({ [key]: v } as Partial<ThemeTokens>)}
        />
      ))}

      <button
        type="button"
        className="ghost"
        style={{ marginTop: 8, width: '100%', justifyContent: 'center' }}
        onClick={() => setTheme({ ...theme, tokens: deriveNeutrals(theme.tokens, theme.mode) })}
        title="Recalcula as cores secundárias a partir das cinco acima"
      >
        derivar as cores secundárias
      </button>
      <p className="card-desc" style={{ marginTop: 6 }}>
        Regenera fundo do terminal, superfícies, bordas e tons de texto a partir
        das cinco cores acima — útil depois de mexer no fundo ou no acento.
      </p>

      {(accentOnBg < 4.5 || textOnAccent < 4.5) && (
        <div className="warn-box card">
          {accentOnBg < 4.5 && (
            <>
              O acento tem pouco contraste com o fundo ({accentOnBg.toFixed(1)}:1); links e
              abas ficam difíceis de ler.{' '}
            </>
          )}
          {textOnAccent < 4.5 && (
            <>
              O texto sobre o acento está em {textOnAccent.toFixed(1)}:1 — o botão principal
              fica ilegível. &ldquo;Derivar as cores secundárias&rdquo; corrige.
            </>
          )}
        </div>
      )}

      <button
        type="button"
        className="ghost"
        style={{ marginTop: 10 }}
        onClick={() => setAdvanced((v) => !v)}
      >
        {advanced ? '▾' : '▸'} avançado
      </button>

      {advanced && (
        <div style={{ marginTop: 6 }}>
          {ADVANCED_TOKENS.map(({ key, label }) => (
            <TokenRow
              key={key}
              label={label}
              value={theme.tokens[key]}
              onChange={(v) => patch({ [key]: v } as Partial<ThemeTokens>)}
            />
          ))}
        </div>
      )}

      <div className="row" style={{ marginTop: 12 }}>
        <span className="grow" />
        <button
          type="button"
          onClick={() => setTheme(PRESETS[0])}
          disabled={theme.id === PRESETS[0].id}
        >
          Restaurar padrão
        </button>
      </div>

      <p className="card-desc" style={{ marginTop: 10 }}>
        O tema vale neste navegador. O nome e a cor de cada pane também — use o
        duplo clique no título do pane e o botão ◨ ao lado.
      </p>
    </aside>
  );
}

/** Uma linha do ajuste fino: swatch nativo + campo hex espelhado. */
function TokenRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (hex: string) => void;
}) {
  // O campo de texto tem estado proprio para o usuario poder digitar um hex
  // incompleto ("#7f") sem que cada tecla tente pintar a interface.
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? value;

  function commit(raw: string) {
    setDraft(null);
    if (isHex(raw.trim())) onChange(normalizeHex(raw.trim()));
  }

  return (
    <div className="token-row">
      <label>{label}</label>
      <input
        type="color"
        value={isHex(value) ? normalizeHex(value) : '#000000'}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
      />
      <input
        className="hex-input"
        value={shown}
        maxLength={7}
        spellCheck={false}
        aria-label={`${label} em hexadecimal`}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit((e.target as HTMLInputElement).value);
          if (e.key === 'Escape') setDraft(null);
        }}
      />
    </div>
  );
}
